/**
 * Climate Indicators Reference
 * 
 * This file contains mappings and definitions for climate indicators
 * used in weather overlays and climate data visualization.
 */

export interface ClimateIndicator {
    acronym: string;
    name: string;
}

/**
 * Climate Indicators Map
 * Key: Acronym, Value: Full name/description
 */
export const CLIMATE_INDICATORS: Record<string, ClimateIndicator> = {
    climate_t_v2: {
        acronym: 'climate_t_v2',
        name: 'Changing temperature air, Changing temperature freshwater - Mean of daily mean temperature (T)'
    },
    // climate_tas: {
    //     acronym: 'climate_tas',
    //     name: 'Mean temperature'
    // },
    // climate_tasmax: {
    //     acronym: 'climate_tasmax',
    //     name: 'Maximum temperature'
    // },
    // climate_tasmin: {
    //     acronym: 'climate_tasmin',
    //     name: 'Minimum temperature'
    // },
    climate_fd_v2: {
        acronym: 'climate_fd_v2',
        name: 'Cold wave/frost - Frost days (FD) (minimum temperature below 0°C)'
    },
    climate_cd_v2: {
        acronym: 'climate_cd_v2',
        name: 'Changing temperature air - Cooling degree days (CD)'
    },
    climate_hd_v2: {
        acronym: 'climate_hd_v2',
        name: 'Changing temperature air - Heating degree days (HD)'
    },
    climate_txx_v2: {
        acronym: 'climate_txx_v2',
        name: 'Heat Stress - Maximum of daily maximum temperature (TXx)'
    },
    climate_tx35_v2: {
        acronym: 'climate_tx35_v2',
        name: 'Heat wave - Number of days with maximum temperature above 35°C (Bias Adjusted TX35)'
    },
    climate_tx40_v2: {
        acronym: 'climate_tx40_v2',
        name: 'Heat wave - Number of days with maximum temperature above 40°C (Bias Adjusted TX40)'
    },
    climate_pr_v2: {
        acronym: 'climate_pr_v2',
        name: 'Changing precipitation patterns rain - Mean of daily accumulated precipitation (PR)'
    },
    climate_cdd_v2: {
        acronym: 'climate_cdd_v2',
        name: 'Wildfire - Maximum consecutive dry days (CDD)'
    },
    climate_rx1day_v2: {
        acronym: 'climate_rx1day_v2',
        name: 'Flood, Heavy precipitation - Maximum of 1-day accumulated precipitation (RX1day)'
    },
    climate_rx5day_v2: {
        acronym: 'climate_rx5day_v2',
        name: 'Flood, Landslide, Subsidence - Maximum of 5-day accumulated precipitation (RX5day)'
    },
    climate_spi6_v2: {
        acronym: 'climate_spi6_v2',
        name: 'Subsidence, Water Stress - Standardised Precipitation Index (SPI-6)'
    },
    climate_prsn_v2: {
        acronym: 'climate_prsn_v2',
        name: 'Changing precipitation patterns snow - Mean of daily accumulated snowfall precipitation (PRSN)'
    },
    climate_sfcwind_v2: {
        acronym: 'climate_sfcwind_v2',
        name: 'Changing Wind Patterns - Mean of daily mean wind speed (SFCWIND)'
    },
    climate_sst_v2: {
        acronym: 'climate_sst_v2',
        name: 'Changing Temperature Marine Water - Mean Sea Surface Temperature (SST)'
    },
    climate_tr_v2: {
        acronym: 'climate_tr_v2',
        name: 'Tropical Nights - Number of days with minimum temperature above 20°C (TR)'
    },
    climate_slr_v2: {
        acronym: 'climate_slr_v2',
        name: 'Sea Level Rise - Mean Sea Level Rise (SLR)'
    }
};

/**
 * Array of all climate indicator acronyms
 */
export const CLIMATE_INDICATOR_ACRONYMS = Object.keys(CLIMATE_INDICATORS);

/**
 * Get climate indicator by acronym
 * @param acronym - The indicator acronym (e.g., 't', 'fd', 'pr')
 * @returns ClimateIndicator object or undefined if not found
 */
export function getClimateIndicator(acronym: string): ClimateIndicator | undefined {
    return CLIMATE_INDICATORS[acronym.toLowerCase()];
}

/**
 * Get climate indicator name by acronym
 * @param acronym - The indicator acronym
 * @returns Full name of the indicator or the acronym if not found
 */
export function getClimateIndicatorName(acronym: string): string {
    const indicator = CLIMATE_INDICATORS[acronym.toLowerCase()];
    return indicator ? indicator.name : acronym;
}

/**
 * Check if an acronym is a valid climate indicator
 * @param acronym - The acronym to check
 * @returns true if valid, false otherwise
 */
export function isValidClimateIndicator(acronym: string): boolean {
    return acronym.toLowerCase() in CLIMATE_INDICATORS;
}

export const CLIMATE_INDICATORS_LEGEND: Record<string, ClimateIndicator> = {
    climate_t_v2: {
        acronym: 'climate_t_v2',
        name: 'Changing temperature air, Changing temperature freshwater - Mean of daily mean temperature (T)'
    },
    climate_fd_v2: {
        acronym: 'climate_fd_v2',
        name: 'Cold wave/frost - Frost days (FD) (minimum temperature below 0°C)'
    },
    climate_cd_v2: {
        acronym: 'climate_cd_v2',
        name: 'Changing temperature air - Cooling degree days (CD)'
    },
    climate_hd_v2: {
        acronym: 'climate_hd_v2',
        name: 'Changing temperature air - Heating degree days (HD)'
    },
    climate_txx_v2: {
        acronym: 'climate_txx_v2',
        name: 'Heat Stress - Maximum of daily maximum temperature (TXx)'
    },
    climate_tx35_v2: {
        acronym: 'climate_tx35_v2',
        name: 'Heat wave - Number of days with maximum temperature above 35°C (Bias Adjusted TX35)'
    },
    climate_tx40_v2: {
        acronym: 'climate_tx40_v2',
        name: 'Heat wave - Number of days with maximum temperature above 40°C (Bias Adjusted TX40)'
    },
    climate_pr_v2: {
        acronym: 'climate_pr_v2',
        name: 'Changing precipitation patterns rain - Mean of daily accumulated precipitation (PR)'
    },
    climate_cdd_v2: {
        acronym: 'climate_cdd_v2',
        name: 'Wildfire - Maximum consecutive dry days (CDD)'
    },
    climate_rx1day_v2: {
        acronym: 'climate_rx1day_v2',
        name: 'Flood, Heavy precipitation - Maximum of 1-day accumulated precipitation (RX1day)'
    },
    climate_rx5day_v2: {
        acronym: 'climate_rx5day_v2',
        name: 'Flood, Landslide, Subsidence - Maximum of 5-day accumulated precipitation (RX5day)'
    },
    climate_spi6_v2: {
        acronym: 'climate_spi6_v2',
        name: 'Subsidence, Water Stress - Standardised Precipitation Index (SPI-6)'
    },
    climate_prsn_v2: {
        acronym: 'climate_prsn_v2',
        name: 'Changing precipitation patterns snow - Mean of daily accumulated snowfall precipitation (PRSN)'
    },
    climate_sfcwind_v2: {
        acronym: 'climate_sfcwind_v2',
        name: 'Changing Wind Patterns - Mean of daily mean wind speed (SFCWIND)'
    },
    climate_sst_v2: {
        acronym: 'climate_sst_v2',
        name: 'Changing Temperature Marine Water - Mean Sea Surface Temperature (SST)'
    },
    climate_tr_v2: {
        acronym: 'climate_tr_v2',
        name: 'Tropical Nights - Number of days with minimum temperature above 20°C (TR)'
    },
    climate_slr_v2: {
        acronym: 'climate_slr_v2',
        name: 'Sea Level Rise - Mean Sea Level Rise (SLR)'
    },
     climate_change_t: {
        acronym: 'climate_change_t',
        name: 'Changing temperature air, Changing temperature freshwater - Mean of daily mean temperature (T)'
    },
    climate_fd_change: {
        acronym: 'climate_fd_change',
        name: 'Cold wave/frost - Frost days (FD) (minimum temperature below 0°C)'
    },
    climate_cd_change: {
        acronym: 'climate_cd_change',
        name: 'Changing temperature air - Cooling degree days (CD)'
    },
    climate_hd_change: {
        acronym: 'climate_hd_change',
        name: 'Changing temperature air - Heating degree days (HD)'
    },
    climate_txx_change: {
        acronym: 'climate_txx_change',
        name: 'Heat Stress - Maximum of daily maximum temperature (TXx)'
    },
    climate_tx35_change: {
        acronym: 'climate_tx35_change',
        name: 'Heat wave - Number of days with maximum temperature above 35°C (Bias Adjusted TX35)'
    },
    climate_tx40_change: {
        acronym: 'climate_tx40_change',
        name: 'Heat wave - Number of days with maximum temperature above 40°C (Bias Adjusted TX40)'
    },
    climate_pr_change: {
        acronym: 'climate_pr_change',
        name: 'Changing precipitation patterns rain - Mean of daily accumulated precipitation (PR)'
    },
    climate_cdd_change: {
        acronym: 'climate_cdd_change',
        name: 'Wildfire - Maximum consecutive dry days (CDD)'
    },
    climate_rx1day_change: {
        acronym: 'climate_rx1day_change',
        name: 'Flood, Heavy precipitation - Maximum of 1-day accumulated precipitation (RX1day)'
    },
    climate_rx5day_change: {
        acronym: 'climate_rx5day_change',
        name: 'Flood, Landslide, Subsidence - Maximum of 5-day accumulated precipitation (RX5day)'
    },
    climate_spi6_change: {  
        acronym: 'climate_spi6_change',
        name: 'Subsidence, Water Stress - Standardised Precipitation Index (SPI-6)'
    },
    climate_prsn_change: {
        acronym: 'climate_prsn_change',
        name: 'Changing precipitation patterns snow - Mean of daily accumulated snowfall precipitation (PRSN)'
    },
    climate_sfcwind_change: {       
        acronym: 'climate_sfcwind_change',
        name: 'Changing Wind Patterns - Mean of daily mean wind speed (SFCWIND)'
    },
    climate_sst_change: {
        acronym: 'climate_sst_change',
        name: 'Changing Temperature Marine Water - Mean Sea Surface Temperature (SST)'
    },
    climate_t_change_v2: {
        acronym: 'climate_t_change_v2',
        name: 'Changing temperature air, Changing temperature freshwater - Mean of daily mean temperature (T)'
    },
    climate_fd_change_v2: {
        acronym: 'climate_fd_change_v2',
        name: 'Cold wave/frost - Frost days (FD) (minimum temperature below 0°C)'
    },
    climate_change_cd_v2: {
        acronym: 'climate_change_cd_v2',
        name: 'Changing temperature air - Cooling degree days (CD)'
    },
    climate_hd_change_v2: {
        acronym: 'climate_hd_change_v2',
        name: 'Changing temperature air - Heating degree days (HD)'
    },
    climate_txx_change_v2: {
        acronym: 'climate_txx_change_v2',
        name: 'Heat Stress - Maximum of daily maximum temperature (TXx)'
    },
    climate_tx35_change_v2: {
        acronym: 'climate_tx35_change_v2',
        name: 'Heat wave - Number of days with maximum temperature above 35°C (Bias Adjusted TX35)'
    },
    climate_tx40_change_v2: {
        acronym: 'climate_tx40_change_v2',
        name: 'Heat wave - Number of days with maximum temperature above 40°C (Bias Adjusted TX40)'
    },
    climate_pr_change_v2: {
        acronym: 'climate_pr_change_v2',
        name: 'Changing precipitation patterns rain - Mean of daily accumulated precipitation (PR)'
    },
    climate_cdd_change_v2: {
        acronym: 'climate_cdd_change_v2',
        name: 'Wildfire - Maximum consecutive dry days (CDD)'
    },
    climate_rx1day_change_v2: {
        acronym: 'climate_rx1day_change_v2',
        name: 'Flood, Heavy precipitation - Maximum of 1-day accumulated precipitation (RX1day)'
    },
    climate_rx5day_change_v2: {
        acronym: 'climate_rx5day_change_v2',
        name: 'Flood, Landslide, Subsidence - Maximum of 5-day accumulated precipitation (RX5day)'
    },
    climate_spi6_change_v2: {
        acronym: 'climate_spi6_change_v2',
        name: 'Subsidence, Water Stress - Standardised Precipitation Index (SPI-6)'
    },
    climate_prsn_change_v2: {
        acronym: 'climate_prsn_change_v2',
        name: 'Changing precipitation patterns snow - Mean of daily accumulated snowfall precipitation (PRSN)'
    },
    climate_sfcwind_change_v2: {
        acronym: 'climate_sfcwind_change_v2',
        name: 'Changing Wind Patterns - Mean of daily mean wind speed (SFCWIND)'
    },
    climate_sst_change_v2: {
        acronym: 'climate_sst_change_v2',
        name: 'Changing Temperature Marine Water - Mean Sea Surface Temperature (SST)'
    },
    climate_tr_change_v2: {
        acronym: 'climate_tr_change_v2',
        name: 'Tropical Nights - Number of days with minimum temperature above 20°C (TR)'
    },
    climate_slr_change_v2: {
        acronym: 'climate_slr_change_v2',
        name: 'Sea Level Rise - Mean Sea Level Rise (SLR)'
    }
};

/**
 * Frequency-based indicators (Frequency Scale)
 * 
 * These indicators represent frequency of occurrence (number of days).
 * These should use frequency labels instead of intensity labels in the legend.
 * 
 * Frequency-based indicators include:
 * - Cooling degree days (CD)
 * - Heating degree days (HD)
 * - Frost days (FD) - minimum temperature below 0°C
 * - Days with temperature above 35°C (TX35)
 * - Days with temperature above 40°C (TX40)
 */
export const FREQUENCY_INDICATORS: ReadonlyArray<string> = [
    'climate_cd_v2',      // Cooling degree days (CD)
    'climate_hd_v2',      // Heating degree days (HD)
    'climate_fd_v2',      // Frost days (FD)
    'climate_tx35_v2',    // Number of days with maximum temperature above 35°C (TX35)
    'climate_tx40_v2',    // Number of days with maximum temperature above 40°C (TX40)
]

/**
 * SSP Field Name to Display Label Mapping
 * Maps field names like 'SSP585_MT' to human-readable labels
 */
export const SSP_FIELD_LABELS: Record<string, string> = {
    // SSP1-1.9
    'SSP119_NT': 'SSP1-1.9 Near Term (2021-2040) (rel. to 1995 to 2014)',
    'SSP119_MT': 'SSP1-1.9 Medium Term (2041–2060) (rel. to 1995 to 2014)',
    'SSP119_LT': 'SSP1-1.9 Long Term (2081–2100) (rel. to 1995 to 2014)',
    // SSP1-2.6
    'SSP126_NT': 'SSP1-2.6 Near Term (2021-2040) (rel. to 1995 to 2014)',
    'SSP126_MT': 'SSP1-2.6 Medium Term (2041–2060) (rel. to 1995 to 2014)',
    'SSP126_LT': 'SSP1-2.6 Long Term (2081–2100) (rel. to 1995 to 2014)',
    // SSP2-4.5
    'SSP245_NT': 'SSP2-4.5 Near Term (2021-2040) (rel. to 1995 to 2014)',
    'SSP245_MT': 'SSP2-4.5 Medium Term (2041–2060) (rel. to 1995 to 2014)',
    'SSP245_LT': 'SSP2-4.5 Long Term (2081–2100) (rel. to 1995 to 2014)',
    // SSP3-7.0
    'SSP370_NT': 'SSP3-7.0 Near Term (2021-2040) (rel. to 1995 to 2014)',
    'SSP370_MT': 'SSP3-7.0 Medium Term (2041–2060) (rel. to 1995 to 2014)',
    'SSP370_LT': 'SSP3-7.0 Long Term (2081–2100) (rel. to 1995 to 2014)',
    // SSP5-8.5
    'SSP585_NT': 'SSP5-8.5 Near Term (2021-2040) (rel. to 1995 to 2014)',
    'SSP585_MT': 'SSP5-8.5 Medium Term (2041–2060) (rel. to 1995 to 2014)',
    'SSP585_LT': 'SSP5-8.5 Long Term (2081–2100) (rel. to 1995 to 2014)',
};

/**
 * Get the display label for an SSP field name
 * @param fieldName - The field name (e.g., 'SSP585_MT')
 * @returns The display label or the original field name if not found
 */
export function getSSPFieldLabel(fieldName: string): string {
    if (!fieldName) return fieldName;
    const upperFieldName = fieldName.toUpperCase();
    return SSP_FIELD_LABELS[upperFieldName] || fieldName;
}
